/**
 * Question Builder (US P2 — `farmer-form-versioning`).
 *
 * Port của ngăn "Bộ câu hỏi" trong bản mẫu tĩnh, nhưng đổi hẳn chỗ lưu: bản mẫu
 * ghi cấu trúc vào `localStorage` và áp dụng ngay cho chính máy đó. Ở đây R6 chi
 * phối mọi thứ:
 *
 *  - Không bao giờ sửa tại chỗ phiên bản đang được phiếu dùng. Mỗi lần lưu là
 *    `POST /forms/:formKey/versions` → tạo một **bản nháp** mới.
 *  - Bản nháp chỉ có hiệu lực khi `POST /forms/versions/:id/publish`; lúc đó bản
 *    active cũ chuyển sang `archived`, phiếu cũ vẫn mở đúng nhờ
 *    `form_schema_snapshot` của riêng nó.
 *  - Còn lỗi `error` khi kiểm tra logic thì chặn phát hành — một bộ câu hỏi có
 *    tham chiếu sai sẽ tạo ra phiếu không bao giờ điền đủ được.
 */
import * as api from '../api-client.js';
import { FORM_KEYS, FORM_LABELS } from '@farmer/audit-rules';
import { el, esc, toast, formatDateTime } from '../ui/util.js';
import { lintForm, lintSummary } from '../engine/form-lint.js';
import { renderThresholdEditor } from './audit-thresholds.js';
import {
  blankRow, buildBuilderTree, deleteNode, findNode, groupOptions,
  insertRows, moveNode, renameReferences, replaceRow, usedNames,
} from '../engine/builder-tree.js';
import {
  SEARCHABLE_OPTION_THRESHOLD, optionSearchMode, selectOneMode, withSelectOneMode,
} from '../engine/question-type.js';
import {
  DEFAULT_VALUE_COLUMN, hasDefaultValue, prefillColumns, prefillSourceOf, prefillSources,
} from '../engine/prefill.js';

/**
 * Giá trị ở đây là **loại của trình soạn**, không phải cột `type` của XLSForm:
 * `select_one_radio` và `select_one` cùng lưu ra `select_one <danh sách>`, chỉ khác
 * cột `appearance` (xem engine/question-type.js).
 */
const QUESTION_TYPES = [
  ['text', 'Văn bản'],
  ['integer', 'Số nguyên'],
  ['decimal', 'Số thập phân'],
  ['date', 'Ngày'],
  ['select_one_radio', 'Radio Button (Chọn một)'],
  ['select_one', 'Chọn từ danh sách'],
  ['select_multiple', 'Chọn nhiều'],
  ['note', 'Ghi chú (không nhập)'],
  ['calculate', 'Tính toán (ẩn)'],
  ['geopoint', 'Toạ độ GPS'],
];

const SEARCH_MODES = [
  ['auto', `Tự động — hiện khi danh sách có từ ${SEARCHABLE_OPTION_THRESHOLD} lựa chọn trở lên`],
  ['yes', 'Luôn hiện ô tìm kiếm'],
  ['no', 'Không hiện ô tìm kiếm'],
];

/**
 * Dạng ô soạn danh sách lựa chọn: cột thứ ba (nếu có) ghi vào cột nào của sheet
 * `choices`. `column` rỗng = chỉ `giá trị | nhãn`.
 */
const LIST_FORMATS = [
  {
    column: '',
    label: 'Giá trị | Nhãn',
    placeholder: '1 | Có\n0 | Không',
    hint: 'Định dạng: <code>giá trị | nhãn hiển thị</code>. Giá trị là thứ được lưu vào dữ liệu.',
  },
  {
    column: 'category',
    label: 'Giá trị | Nhãn | Nhóm',
    placeholder: '1 | Có | nhom_a\n0 | Không | nhom_b',
    hint: 'Định dạng: <code>giá trị | nhãn hiển thị | nhóm</code>. Giá trị là thứ được lưu vào dữ liệu;'
      + ' <b>nhóm</b> là thứ các câu hỏi khác dùng để lọc hoặc để bật điều kiện hiển thị — xoá nhóm đi là câu hỏi phụ thuộc sẽ không bao giờ hiện.',
  },
  {
    column: DEFAULT_VALUE_COLUMN,
    label: 'Giá trị | Nhãn | Dữ liệu mặc định',
    placeholder: 'T001 | Regent 800WG | 80\nT002 | Actara 25WG | 25',
    hint: 'Định dạng: <code>giá trị | nhãn hiển thị | dữ liệu mặc định</code>. Giá trị là thứ được lưu vào dữ liệu;'
      + ' <b>dữ liệu mặc định</b> được điền sẵn vào câu hỏi khác chọn "Hiển thị theo dữ liệu mặc định" của câu này (để trống nếu không có).',
  },
];
const listFormat = (column) => LIST_FORMATS.find((f) => f.column === column) || LIST_FORMATS[0];

const KIND_LABEL = { group: 'Group', repeat: 'Group lặp', question: 'Câu hỏi' };

/** Loại của trình soạn suy ngược từ một dòng schema. */
function builderTypeOf(row) {
  const base = String(row.type || 'text').trim().split(/\s+/)[0];
  return base === 'select_one' && selectOneMode(row) === 'radio' ? 'select_one_radio' : base;
}

export async function renderFormBuilder(container) {
  const user = api.getCurrentUser();
  if (!user || user.role !== 'admin') {
    container.innerHTML = `<div class="empty-state"><span class="ms ms-xl">lock</span>
      <p>Chỉ tài khoản quản trị mới được sửa bộ câu hỏi.</p></div>`;
    return;
  }

  // Trạng thái đang chỉnh sửa, tách khỏi dữ liệu đã lưu trên máy chủ.
  let formKey = 'onfarm';
  let survey = [];
  let choices = [];
  let auditRules = {};
  let versions = [];
  let editing = null; // phiên bản đang mở trong trình soạn (draft hay active)
  let dirty = false;

  container.innerHTML = `
    <div class="page-head">
      <div>
        <h1>Bộ câu hỏi điều tra</h1>
        <p>Tạo Group, nhóm nhập lặp và câu hỏi. Bấm lưu là ghi thẳng vào phiên bản đang mở —
           <b>phiếu tạo mới từ lúc đó dùng ngay bộ câu hỏi này</b>; phiếu đã tạo giữ nguyên bản của chúng.</p>
      </div>
      <div class="page-head-actions">
        <select id="bFormKey" class="filters-select">
          ${FORM_KEYS.map((k) => `<option value="${esc(k)}">${esc(FORM_LABELS[k].label)}</option>`).join('')}
        </select>
        <button class="btn btn-tonal" id="bSaveBtn"><span class="ms">save</span>Lưu và áp dụng</button>
      </div>
    </div>

    <div class="builder-tabs" role="tablist">
      <button class="builder-tab active" data-tab="questions" role="tab" aria-selected="true">
        <span class="ms ms-sm">quiz</span>Câu hỏi
      </button>
      <button class="builder-tab" data-tab="thresholds" role="tab" aria-selected="false">
        <span class="ms ms-sm">tune</span>Ngưỡng đối chiếu
      </button>
    </div>

    <div class="builder-toolbar" id="bQuestionToolbar">
      <button class="btn btn-tonal sm" id="bAddGroup"><span class="ms">create_new_folder</span>Thêm Group</button>
      <button class="btn btn-tonal sm" id="bAddRepeat"><span class="ms">repeat</span>Thêm Group lặp</button>
      <button class="btn btn-outlined sm" id="bAddQuestion"><span class="ms">add</span>Thêm câu hỏi</button>
      <button class="btn btn-text sm" id="bReload"><span class="ms">restart_alt</span>Nạp lại từ máy chủ</button>
      <span class="builder-status" id="bStatus"></span>
    </div>

    <div id="bThresholdPanel" hidden></div>

    <div class="builder-layout" id="bQuestionPanel">
      <div class="card">
        <div class="builder-tree" id="bTree"></div>
      </div>
      <div>
        <div class="card">
          <h3 class="card-title"><span class="ms">rule</span>Kiểm tra logic</h3>
          <div class="lint-summary" id="bLintSummary"></div>
          <div id="bLintList"></div>
        </div>
        <div class="card">
          <h3 class="card-title"><span class="ms">history</span>Phiên bản</h3>
          <div id="bVersions"></div>
        </div>
      </div>
    </div>`;

  const treeHost = container.querySelector('#bTree');
  const statusEl = container.querySelector('#bStatus');

  // ------------------------------------------------------------------ tab
  // Ngưỡng nằm cùng màn hình với câu hỏi vì chúng là dữ liệu của CÙNG một phiên
  // bản: lưu một lần ghi cả hai, publish một lần áp dụng cả hai. Tách ra hai màn
  // hình sẽ gợi ý rằng chúng có vòng đời riêng, mà không phải vậy.
  const thresholdPanel = container.querySelector('#bThresholdPanel');
  const questionPanel = container.querySelector('#bQuestionPanel');
  const questionToolbar = container.querySelector('#bQuestionToolbar');

  function switchTab(tab) {
    const laNguong = tab === 'thresholds';
    thresholdPanel.hidden = !laNguong;
    questionPanel.hidden = laNguong;
    questionToolbar.hidden = laNguong;
    container.querySelectorAll('.builder-tab').forEach((b) => {
      const chon = b.dataset.tab === tab;
      b.classList.toggle('active', chon);
      b.setAttribute('aria-selected', String(chon));
    });
    if (laNguong) veNguong();
  }

  /** Vẽ bảng ngưỡng của bộ câu hỏi đang mở. Gọi cả khi đổi bộ ở select, vì bảng chỉ
   *  liệt kê chỉ số mà bộ luật của bộ đó đọc tới — để nguyên là hiện nhầm bộ. */
  function veNguong() {
    // Không dùng `setDirty()`: nó gọi `renderAll()`, tức vẽ lại cả cây câu hỏi
    // sau MỖI phím gõ vào ô ngưỡng. Ở đây chỉ cần đánh dấu và đổi nhãn nút lưu.
    renderThresholdEditor(thresholdPanel, auditRules, formKey, () => {
      dirty = true;
      statusEl.textContent = 'Có thay đổi chưa lưu';
      statusEl.className = 'builder-status is-dirty';
      renderSaveButton();
    });
  }

  container.querySelectorAll('.builder-tab').forEach((btn) =>
    btn.addEventListener('click', () => switchTab(btn.dataset.tab)),
  );

  // ------------------------------------------------------------------ tải
  async function load(key) {
    formKey = key;
    treeHost.innerHTML = '<div class="empty-state">Đang tải bộ câu hỏi...</div>';
    try {
      versions = await api.forms.versions(formKey);
      // Mở BẢN NHÁP mới nhất trước, chỉ rơi về bản đang áp dụng khi không có nháp nào.
      // Trước đây luôn mở bản `active`: admin sửa câu hỏi, bấm "Lưu thành bản nháp"
      // (lưu đúng, tạo v2), rồi quay lại trang thì thấy y nguyên bản cũ — trông hệt
      // như hệ thống đã nuốt mất thay đổi. `versions` được API trả về theo số hiệu
      // giảm dần nên `find` đầu tiên chính là bản nháp mới nhất.
      openVersion(versions.find((v) => v.status === 'draft')
        || versions.find((v) => v.status === 'active')
        || versions[0]);
    } catch (err) {
      treeHost.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
    }
  }

  /** Nạp một phiên bản vào trình soạn. Một chỗ duy nhất đặt `editing`, để thanh
   *  trạng thái không bao giờ nói sai bản nào đang mở. */
  function openVersion(version) {
    // Loại phiếu mới (ba phiếu RCP trước lát 4) có định nghĩa nhưng chưa có phiên bản nào:
    // mở trình soạn trống, lần lưu đầu tạo bản nháp v1 (save() thấy `editing` rỗng thì tạo mới).
    editing = version || null;
    survey = structuredClone(version?.surveySchema || []);
    choices = structuredClone(version?.choicesSchema || []);
    auditRules = structuredClone(version?.auditRulesSnapshot || {});
    dirty = false;
    renderAll();
  }

  function setDirty(msg) {
    dirty = true;
    statusEl.textContent = msg || 'Có thay đổi chưa lưu';
    statusEl.className = 'builder-status is-dirty';
    renderAll();
  }

  /** Nhãn nút lưu phải nói đúng chuyện sắp xảy ra — ba kết cục rất khác nhau. */
  function renderSaveButton() {
    const btn = container.querySelector('#bSaveBtn');
    const label = !editing || editing.isImmutable
      ? 'Lưu thành phiên bản mới'
      : editing.status === 'active'
        ? 'Lưu và áp dụng'
        : editing.status === 'draft'
          ? 'Lưu bản nháp'
          : 'Lưu vào bản này';
    btn.innerHTML = `<span class="ms">save</span>${label}`;
    btn.title = editing?.isImmutable
      ? 'Phiên bản đang mở đã có phiếu dùng nên không ghi đè được — lưu sẽ sinh một phiên bản nháp mới.'
      : editing?.status === 'active'
        ? 'Ghi thẳng vào phiên bản đang áp dụng. Phiếu tạo mới từ lúc này sẽ dùng ngay bộ câu hỏi vừa lưu.'
        : 'Ghi thẳng vào phiên bản đang mở.';
  }

  function renderAll() {
    renderTree();
    renderLint();
    renderVersions();
    renderSaveButton();
    // Bảng ngưỡng gắn với cả phiên bản lẫn bộ câu hỏi đang mở, nên phải vẽ lại cùng
    // phần còn lại — nếu không, đổi bộ ở select trong lúc đang ở tab Ngưỡng sẽ để lại
    // bảng của bộ cũ, mà mọi ô trên đó lại ghi vào snapshot của bộ mới.
    if (!thresholdPanel.hidden) veNguong();
    if (!dirty) {
      // Nói rõ đang mở bản nào: nháp và bản đang áp dụng nhìn giống hệt nhau trên cây,
      // mà hậu quả của việc nhầm thì rất khác.
      statusEl.textContent = !editing
        ? 'Chưa có phiên bản nào — soạn câu hỏi rồi bấm Lưu để tạo bản nháp đầu tiên'
        : editing.status === 'draft'
          ? `Đang sửa bản nháp v${editing.versionNumber} — chưa phát hành`
          : `Đang xem phiên bản đang áp dụng v${editing.versionNumber}`;
      statusEl.className = 'builder-status';
    }
  }

  // ------------------------------------------------------------------ cây
  function renderTree() {
    const root = buildBuilderTree(survey);
    treeHost.innerHTML = '';
    if (!root.children.length) {
      treeHost.innerHTML = `<div class="empty-state">Bộ câu hỏi đang trống. Dùng "Thêm Group" để bắt đầu.</div>`;
      return;
    }
    root.children.forEach((node, i) => treeHost.appendChild(renderNode(node, i, root.children, 0)));
  }

  function renderNode(node, index, siblings, depth) {
    const wrap = el('div', `builder-node builder-${node.kind}`);
    wrap.style.marginLeft = `${depth * 16}px`;

    const isGroup = node.kind !== 'question';
    const display = isGroup
      ? String(node.row.ui_display || (node.kind === 'repeat' ? 'cards' : 'list'))
      : String(node.row.type || '') + (builderTypeOf(node.row) === 'select_one_radio' ? ' · radio' : '');

    const row = el('div', 'builder-row');
    row.innerHTML = `
      <span class="ms builder-icon">${isGroup ? (node.kind === 'repeat' ? 'repeat' : 'folder') : 'help'}</span>
      <span class="builder-main">
        <span class="builder-label">${esc(node.row.label || node.row.name || '(chưa đặt tên)')}</span>
        <span class="builder-meta">
          <code>${esc(node.row.name || '—')}</code>
          <span class="builder-kind">${esc(KIND_LABEL[node.kind])}</span>
          <span class="builder-display">${esc(display)}</span>
          ${node.row.relevant ? `<span class="builder-flag" title="Có điều kiện hiển thị">relevant</span>` : ''}
          ${String(node.row.required || '').toLowerCase() === 'yes' ? '<span class="builder-flag req">bắt buộc</span>' : ''}
        </span>
      </span>
      <span class="builder-actions">
        <button class="icon-btn" data-act="up" ${index === 0 ? 'disabled' : ''} title="Lên"><span class="ms">arrow_upward</span></button>
        <button class="icon-btn" data-act="down" ${index === siblings.length - 1 ? 'disabled' : ''} title="Xuống"><span class="ms">arrow_downward</span></button>
        ${isGroup ? '<button class="icon-btn" data-act="add-child" title="Thêm câu hỏi vào nhóm này"><span class="ms">add</span></button>' : ''}
        <button class="icon-btn" data-act="edit" title="Sửa"><span class="ms">edit</span></button>
        <button class="icon-btn danger" data-act="del" title="Xoá"><span class="ms">delete</span></button>
      </span>`;

    row.querySelectorAll('[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => handleAction(btn.dataset.act, node));
    });
    wrap.appendChild(row);

    node.children.forEach((c, i) => wrap.appendChild(renderNode(c, i, node.children, depth + 1)));
    return wrap;
  }

  function handleAction(act, node) {
    if (act === 'up' || act === 'down') {
      survey = moveNode(survey, node.start, act === 'up' ? -1 : 1);
      setDirty('Đã đổi thứ tự');
      return;
    }
    if (act === 'del') {
      const what = node.kind === 'question' ? 'câu hỏi' : `nhóm này và toàn bộ ${node.children.length} mục bên trong`;
      if (!window.confirm(`Xoá ${what}? Phiếu đã nộp không bị ảnh hưởng, nhưng phiếu mới sẽ không còn ô này.`)) return;
      survey = deleteNode(survey, node.start);
      setDirty('Đã xoá');
      return;
    }
    if (act === 'add-child') {
      openQuestionEditor({ parentStart: node.start });
      return;
    }
    if (act === 'edit') {
      if (node.kind === 'question') openQuestionEditor({ editStart: node.start });
      else openGroupEditor({ editStart: node.start, kind: node.kind });
    }
  }

  // -------------------------------------------------------------- trình sửa
  function openDialog(title, bodyHtml, onSubmit) {
    const backdrop = el('div', 'dialog-backdrop');
    const form = el('form', 'dialog');
    form.noValidate = true;
    form.innerHTML = `
      <div class="dialog-head"><h3>${esc(title)}</h3>
        <button class="icon-btn" type="button" data-act="close" aria-label="Đóng"><span class="ms">close</span></button></div>
      <div class="dialog-body">${bodyHtml}</div>
      <div class="dialog-error error-text" hidden></div>
      <div class="dialog-actions">
        <button class="btn btn-text" type="button" data-act="cancel">Huỷ</button>
        <!-- "Xong" chứ không phải "Lưu": hộp thoại chỉ ghi vào bản đang soạn trong
             trình duyệt. Chỉ nút "Lưu và áp dụng" ở đầu trang mới gửi lên máy chủ. -->
        <button class="btn" type="submit"><span class="ms">check</span>Xong</button>
      </div>`;
    backdrop.appendChild(form);
    document.body.appendChild(backdrop);

    const close = () => backdrop.remove();
    form.querySelector('[data-act="close"]').addEventListener('click', close);
    form.querySelector('[data-act="cancel"]').addEventListener('click', close);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const errBox = form.querySelector('.dialog-error');
      errBox.hidden = true;
      const err = onSubmit(form);
      if (err) {
        errBox.textContent = err;
        errBox.hidden = false;
        return;
      }
      close();
    });
    form.querySelector('input, select, textarea')?.focus();
    return { form, close };
  }

  const field = (id, label, value = '', opts = {}) => `
    <div class="form-row">
      <label for="${id}">${esc(label)}</label>
      ${
        opts.textarea
          ? `<textarea id="${id}" rows="${opts.rows || 2}" placeholder="${esc(opts.placeholder || '')}">${esc(value)}</textarea>`
          : `<input id="${id}" type="text" value="${esc(value)}" placeholder="${esc(opts.placeholder || '')}">`
      }
      ${opts.hint ? `<span class="hint-text">${opts.hint}</span>` : ''}
    </div>`;

  const selectField = (id, label, options, value) => `
    <div class="form-row">
      <label for="${id}">${esc(label)}</label>
      <select id="${id}">
        ${options.map(([v, t]) => `<option value="${esc(v)}" ${String(v) === String(value) ? 'selected' : ''}>${esc(t)}</option>`).join('')}
      </select>
    </div>`;

  function parentSelect(id, currentParentStart) {
    const opts = [['', '— Ngoài cùng (không thuộc nhóm nào) —'], ...groupOptions(survey).map((g) => [String(g.start), g.label])];
    return selectField(id, 'Nằm trong nhóm', opts, currentParentStart === null ? '' : String(currentParentStart));
  }

  // ---- Group / Group lặp
  function openGroupEditor({ editStart = null, kind = 'group', parentStart = null } = {}) {
    const node = editStart !== null ? findNode(survey, editStart) : null;
    const row = node ? node.row : blankRow();
    const nodeKind = node ? node.kind : kind;
    const isRepeat = nodeKind === 'repeat';

    const body = `
      ${field('gLabel', 'Tiêu đề hiển thị *', row.label, { placeholder: 'VD: VII. Thông tin sử dụng phân bón.' })}
      ${field('gName', 'Tên biến *', row.name, { hint: 'Không dấu, không khoảng trắng. Dùng làm tiền tố khoá dữ liệu.' })}
      ${selectField('gDisplay', 'Kiểu hiển thị', isRepeat
        ? [['cards', 'Thẻ theo từng bản ghi'], ['table', 'Bảng — mỗi bản ghi là một hàng']]
        : [['list', 'Danh sách câu hỏi'], ['table', 'Bảng — mỗi câu hỏi là một cột']],
        row.ui_display || (isRepeat ? 'cards' : 'list'))}
      ${isRepeat ? field('gCount', 'Số dòng tự động (repeat_count)', row.repeat_count, {
        hint: 'Để trống nếu người nhập tự bấm "Thêm dòng". VD: <code>count-selected(${residue_type})</code>',
      }) : ''}
      ${field('gRelevant', 'Điều kiện hiển thị (relevant)', row.relevant, {
        placeholder: '${pesticide_yn} = 1', hint: 'Để trống nếu luôn hiện.',
      })}
      ${editStart === null ? parentSelect('gParent', parentStart) : ''}`;

    openDialog(node ? `Sửa ${KIND_LABEL[nodeKind]}` : `Thêm ${KIND_LABEL[nodeKind]}`, body, (form) => {
      const val = (id) => String(form.querySelector('#' + id)?.value || '').trim();
      const label = val('gLabel');
      const name = val('gName');
      if (!label) return 'Tiêu đề không được để trống.';
      const nameErr = validateName(name, editStart);
      if (nameErr) return nameErr;

      const patch = {
        label,
        name,
        ui_display: val('gDisplay'),
        relevant: val('gRelevant'),
        ...(isRepeat ? { repeat_count: val('gCount') } : {}),
      };

      if (node) {
        const oldName = String(node.row.name || '');
        survey = replaceRow(survey, node.start, patch);
        survey = renameReferences(survey, oldName, name);
        setDirty('Đã cập nhật nhóm');
      } else {
        const [beginType, endType] = isRepeat ? ['begin repeat', 'end repeat'] : ['begin group', 'end group'];
        const parentVal = val('gParent');
        survey = insertRows(survey, parentVal === '' ? null : Number(parentVal), [
          blankRow({ type: beginType, ...patch }),
          blankRow({ type: endType }),
        ]);
        setDirty('Đã thêm nhóm');
      }
      return null;
    });
  }

  // ---- Câu hỏi
  function openQuestionEditor({ editStart = null, parentStart = null } = {}) {
    const node = editStart !== null ? findNode(survey, editStart) : null;
    const row = node ? node.row : blankRow({ type: 'text' });
    const rawType = String(row.type || 'text').trim();
    const builderType = builderTypeOf(row);
    const listName = rawType.split(/\s+/)[1] || '';

    const listRows = choices.filter((c) => c['list name'] === listName);
    // Cột `category` không phải trang trí: `choice_filter` lọc theo nó và các câu
    // `calculate` dạng instance(...)/item[name=${x}]/category đọc nó ra để điều
    // kiện `relevant` so sánh. Danh sách nào đang dùng thì phải cho sửa ngay tại
    // đây — nếu giấu đi, mỗi lần lưu lại câu hỏi là mất trắng cột này và mọi câu
    // phụ thuộc nó ngừng hiện ra. Cột dữ liệu mặc định cũng vậy: câu khác điền sẵn
    // từ nó (engine/prefill.js).
    const initialThird = listRows.some((c) => String(c.category || '').trim())
      ? 'category'
      : hasDefaultValue(listRows) ? DEFAULT_VALUE_COLUMN : '';
    const listText = listRows
      .map((c) => (initialThird ? `${c.name} | ${c.label} | ${c[initialThird] ?? ''}` : `${c.name} | ${c.label}`))
      .join('\n');

    // Nguồn điền sẵn: câu chọn có danh sách mang cột dữ liệu mặc định. Giữ cả nguồn đang
    // dùng dù danh sách của nó đã mất cột, để mở ra sửa không lặng lẽ tháo liên kết.
    const currentPrefill = prefillSourceOf(row);
    const sources = prefillSources(survey, choices).filter((s) => s.name !== row.name);
    if (currentPrefill && !sources.some((s) => s.name === currentPrefill.question)) {
      sources.push({ name: currentPrefill.question, label: currentPrefill.question, list: currentPrefill.list });
    }
    const prefillOptions = [['', '— Không điền sẵn —'],
      ...sources.map((s) => [s.name, `${s.name} — ${s.label}`])];
    const isInputType = (t) => ['text', 'integer', 'decimal'].includes(t);

    const body = `
      ${selectField('qType', 'Loại câu hỏi *', QUESTION_TYPES, builderType)}
      ${field('qLabel', 'Câu hỏi *', row.label, { textarea: true, rows: 2 })}
      ${field('qName', 'Tên biến *', row.name, { hint: 'Khoá lưu trong dữ liệu. Đổi tên sẽ tự cập nhật mọi biểu thức đang trỏ tới.' })}
      ${field('qHint', 'Gợi ý cho điều tra viên', row.hint, { textarea: true, rows: 2 })}
      <div class="form-row form-row-inline">
        <label><input type="checkbox" id="qRequired" ${String(row.required || '').toLowerCase() === 'yes' ? 'checked' : ''}> Bắt buộc nhập</label>
      </div>
      <div data-when-select ${builderType.startsWith('select_') ? '' : 'hidden'}>
        ${field('qList', 'Tên danh sách lựa chọn *', listName, { hint: 'Nhiều câu hỏi có thể dùng chung một danh sách.' })}
        ${selectField('qListFormat', 'Dạng danh sách', LIST_FORMATS.map((f) => [f.column, f.label]), initialThird)}
        ${field('qChoices', 'Các lựa chọn — mỗi dòng một mục *', listText, {
          textarea: true,
          rows: 6,
          placeholder: listFormat(initialThird).placeholder,
          hint: `<span data-list-format-hint>${listFormat(initialThird).hint}</span>`,
        })}
      </div>
      <div data-when-dropdown ${builderType === 'select_one' ? '' : 'hidden'}>
        ${selectField('qSearch', 'Ô tìm kiếm trong danh sách', SEARCH_MODES, optionSearchMode(row))}
      </div>
      ${field('qConstraint', 'Ràng buộc giá trị (constraint)', row.constraint, { placeholder: '. <= 100' })}
      ${field('qConstraintMsg', 'Thông báo khi vi phạm', row.constraint_message)}
      ${field('qRelevant', 'Điều kiện hiển thị (relevant)', row.relevant, { placeholder: '${intercrop_tree_yn} = 1' })}
      <div data-when-no-prefill ${currentPrefill ? 'hidden' : ''}>
        ${field('qCalculation', 'Công thức tính (calculation)', currentPrefill ? '' : row.calculation, { textarea: true, rows: 2 })}
      </div>
      ${field('qDefault', 'Giá trị mặc định', row.default)}
      ${editStart === null ? parentSelect('qParent', parentStart) : ''}
      <div data-when-input ${isInputType(builderType) ? '' : 'hidden'}>
        ${selectField('qPrefill', 'Hiển thị theo dữ liệu mặc định', prefillOptions, currentPrefill?.question || '')}
      </div>`;

    const { form } = openDialog(node ? 'Sửa câu hỏi' : 'Thêm câu hỏi', body, (f) => {
      const val = (id) => String(f.querySelector('#' + id)?.value || '').trim();
      const type = val('qType');
      const label = val('qLabel');
      const name = val('qName');
      if (!label) return 'Nội dung câu hỏi không được để trống.';
      const nameErr = validateName(name, editStart);
      if (nameErr) return nameErr;

      // `type` là loại của trình soạn; radio và danh sách thả xuống cùng ra
      // `select_one`, khác nhau ở `appearance`.
      const xlsType = type === 'select_one_radio' ? 'select_one' : type;
      let fullType = xlsType;
      if (type.startsWith('select_')) {
        const list = val('qList');
        if (!list) return 'Câu hỏi dạng chọn phải có tên danh sách lựa chọn.';
        if (/\s/.test(list)) return 'Tên danh sách lựa chọn không được chứa khoảng trắng.';
        fullType = `${xlsType} ${list}`;
        const third = val('qListFormat');
        const parsed = parseChoiceLines(val('qChoices'), !!third);
        if (!parsed.length) return 'Danh sách lựa chọn phải có ít nhất một dòng.';
        // Giữ lại các cột không hiện trên ô soạn (và cột phụ mà dạng danh sách đang
        // chọn không hiện ra) bằng cách khớp theo `name` với dòng cũ.
        const carry = new Map();
        [listName, list].forEach((ln) => {
          if (ln) choices.filter((c) => c['list name'] === ln).forEach((c) => carry.set(String(c.name), c));
        });
        // Thay trọn danh sách: người dùng vừa nhìn thấy toàn bộ nội dung của nó.
        choices = [...choices.filter((c) => c['list name'] !== list),
          ...parsed.map((p) => {
            const old = carry.get(p.value);
            const next = {
              ...(old || {}),
              'list name': list,
              name: p.value,
              label: p.label,
              category: String(old?.category || ''),
            };
            if (third) next[third] = p.extra;
            return next;
          })];
      }

      // Điền sẵn chỉ có nghĩa với ô nhập tay; đổi sang loại khác thì tháo luôn.
      const prefillQ = isInputType(type) ? val('qPrefill') : '';
      const prefillSrc = sources.find((s) => s.name === prefillQ);
      // Ô công thức bị ẩn (và để trống) khi đang điền sẵn, nên tháo điền sẵn không
      // để lại biểu thức tra cứu biến ô thành ô tính toán bị khoá.
      const calcAndTrigger = prefillSrc
        ? prefillColumns(prefillSrc.name, prefillSrc.list)
        : { calculation: val('qCalculation'), trigger: '' };

      const isSelectOne = type === 'select_one' || type === 'select_one_radio';
      const patch = {
        type: fullType,
        appearance: withSelectOneMode(
          row.appearance,
          isSelectOne ? (type === 'select_one_radio' ? 'radio' : 'dropdown') : null,
        ),
        ui_search: type === 'select_one' ? val('qSearch') : '',
        label,
        name,
        hint: val('qHint'),
        required: f.querySelector('#qRequired').checked ? 'yes' : '',
        required_message: f.querySelector('#qRequired').checked ? 'Vui lòng không được để trống' : '',
        constraint: val('qConstraint'),
        constraint_message: val('qConstraintMsg'),
        relevant: val('qRelevant'),
        ...calcAndTrigger,
        default: val('qDefault'),
      };

      if (node) {
        const oldName = String(node.row.name || '');
        survey = replaceRow(survey, node.start, patch);
        survey = renameReferences(survey, oldName, name);
        setDirty('Đã cập nhật câu hỏi');
      } else {
        const parentVal = val('qParent');
        survey = insertRows(survey, parentVal === '' ? null : Number(parentVal), [blankRow(patch)]);
        setDirty('Đã thêm câu hỏi');
      }
      return null;
    });

    form.querySelector('#qType').addEventListener('change', (e) => {
      form.querySelector('[data-when-select]').hidden = !e.target.value.startsWith('select_');
      form.querySelector('[data-when-dropdown]').hidden = e.target.value !== 'select_one';
      form.querySelector('[data-when-input]').hidden = !isInputType(e.target.value);
    });
    form.querySelector('#qListFormat').addEventListener('change', (e) => {
      const f = listFormat(e.target.value);
      form.querySelector('#qChoices').placeholder = f.placeholder;
      form.querySelector('[data-list-format-hint]').innerHTML = f.hint;
    });
    form.querySelector('#qPrefill').addEventListener('change', (e) => {
      form.querySelector('[data-when-no-prefill]').hidden = !!e.target.value;
    });
  }

  /**
   * `giá trị | nhãn` — hoặc `giá trị | nhãn | cột phụ` (nhóm `category`, dữ liệu mặc định…)
   * theo dạng danh sách đang chọn. Dấu cách quanh `|` là tuỳ ý, mọi phần đều được trim.
   */
  function parseChoiceLines(text, withExtra = false) {
    return String(text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split('|');
        const value = String(parts[0] ?? '').trim();
        // Nhãn có thể chứa dấu `|`; cột phụ thì không, nên cắt từ phải sang.
        const extra = withExtra && parts.length >= 3 ? String(parts.pop()).trim() : '';
        const label = parts.slice(1).join('|').trim();
        return { value, label: label || value, extra };
      })
      .filter((c) => c.value);
  }

  function validateName(name, ignoreStart) {
    if (!name) return 'Tên biến không được để trống.';
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
      return 'Tên biến chỉ gồm chữ không dấu, số và dấu gạch dưới, và không bắt đầu bằng số.';
    }
    if (usedNames(survey, ignoreStart).has(name)) return `Tên biến "${name}" đã được dùng cho câu hỏi khác.`;
    return null;
  }

  // ----------------------------------------------------------- kiểm tra logic
  function renderLint() {
    const issues = lintForm({ survey, choices }, { formKey });
    const { errors, warnings } = lintSummary(issues);
    const summary = container.querySelector('#bLintSummary');
    summary.className = `lint-summary ${errors ? 'has-error' : warnings ? 'has-warn' : 'ok'}`;
    summary.innerHTML = issues.length
      ? `<span class="ms">${errors ? 'error' : 'warning'}</span><span>${errors} lỗi · ${warnings} cảnh báo</span>`
      : `<span class="ms">verified</span><span>Không phát hiện lỗi logic.</span>`;
    container.querySelector('#bLintList').innerHTML = issues
      .map(
        (i) => `<div class="lint-item ${i.sev}"><span class="ms">${i.sev === 'error' ? 'error' : 'warning'}</span>
          <div><span class="lint-row mono">${esc(i.row)} · ${esc(i.col)}</span><div>${esc(i.msg)}</div></div></div>`,
      )
      .join('');
    return { errors, warnings };
  }

  // ---------------------------------------------------------------- phiên bản
  function renderVersions() {
    const host = container.querySelector('#bVersions');
    if (!versions.length) {
      host.innerHTML = '<div class="hint-text">Chưa có phiên bản nào.</div>';
      return;
    }
    host.innerHTML = versions
      .map(
        (v) => `<div class="version-row${v.id === editing?.id ? ' is-open' : ''}" data-version="${esc(v.id)}">
          <span class="badge status-${v.status === 'active' ? 'approved' : v.status === 'draft' ? 'draft' : 'submitted'}">${
            v.status === 'active' ? 'đang áp dụng' : v.status === 'draft' ? 'bản nháp' : 'lưu trữ'
          }</span>
          <span class="version-num">v${v.versionNumber}</span>
          <span class="hint-text">${v.id === editing?.id ? '<b>đang mở</b> · ' : ''}${(v.surveySchema || []).length} dòng${
            v.isImmutable ? ' · đã có phiếu dùng' : ''
          }${v.publishedAt ? ' · ' + esc(formatDateTime(v.publishedAt)) : ''}</span>
          ${v.status === 'draft' ? '<button class="btn btn-tonal sm" data-act="publish">Phát hành</button>' : ''}
          ${v.id === editing?.id ? '' : '<button class="icon-btn" data-act="open" title="Mở bản này để chỉnh sửa"><span class="ms">edit_note</span></button>'}
        </div>`,
      )
      .join('');

    host.querySelectorAll('[data-act="publish"]').forEach((btn) => {
      btn.addEventListener('click', () => publish(btn.closest('[data-version]').dataset.version));
    });
    host.querySelectorAll('[data-act="open"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = versions.find((x) => x.id === btn.closest('[data-version]').dataset.version);
        if (!v) return;
        if (dirty && !window.confirm('Bỏ các thay đổi chưa lưu và mở phiên bản này?')) return;
        openVersion(v);
        toast(`Đang xem phiên bản v${v.versionNumber}`);
      });
    });
  }

  /**
   * Lưu = ghi đè phiên bản đang mở, kể cả khi nó đang áp dụng (PATCH
   * /forms/versions/:id). Quản trị chọn cách này để sửa xong là có hiệu lực ngay,
   * chấp nhận đánh đổi: nội dung cũ bị ghi đè, không còn phiên bản để đối chiếu
   * hay quay lui. Xem chú thích ở FormVersionService.updateVersion.
   *
   * Một trường hợp KHÔNG ghi đè: phiên bản đã có phiếu tham chiếu. Máy chủ chặn
   * bằng CONFLICT, ở đây rẽ sang tạo phiên bản mới để người dùng không mất công
   * nhập lại — nhưng nói rõ là đã sinh bản nháp chứ không phải đã áp dụng.
   */
  async function save() {
    const { errors } = renderLint();
    const target = editing;
    const overwrite = target && !target.isImmutable;
    if (errors > 0) {
      const warn = overwrite && target.status === 'active'
        ? `Còn ${errors} lỗi logic. Vẫn ghi đè phiên bản đang áp dụng v${target.versionNumber}?`
        : `Còn ${errors} lỗi logic. Vẫn lưu?`;
      if (!window.confirm(warn)) return;
    }

    const payload = { surveySchema: survey, choicesSchema: choices, auditRules };
    try {
      if (overwrite) {
        const saved = await api.forms.updateVersion(target.id, payload);
        toast(saved.status === 'active'
          ? `Đã lưu và áp dụng v${saved.versionNumber}`
          : `Đã lưu vào v${saved.versionNumber}`);
        dirty = false;
        versions = await api.forms.versions(formKey);
        editing = versions.find((v) => v.id === saved.id) || saved;
        renderAll();
        return;
      }
      const created = await api.forms.createVersion(formKey, payload);
      toast(target
        ? `Phiên bản cũ đã có phiếu dùng nên không ghi đè được — đã lưu thành bản nháp v${created.versionNumber}. Bấm "Phát hành" để áp dụng.`
        : `Đã lưu bản nháp v${created.versionNumber}. Bấm "Phát hành" để áp dụng.`);
      dirty = false;
      versions = await api.forms.versions(formKey);
      // Chuyển hẳn sang bản vừa lưu: nếu vẫn coi mình đang sửa bản cũ thì lần lưu
      // sau sinh thêm một nháp nữa từ cùng một gốc, và thanh trạng thái nói sai.
      editing = versions.find((v) => v.id === created.id) || created;
      renderAll();
    } catch (err) {
      toast('Không lưu được: ' + err.message);
    }
  }

  async function publish(versionId) {
    const v = versions.find((x) => x.id === versionId);
    const issues = lintForm({ survey: v?.surveySchema || [], choices: v?.choicesSchema || [] }, { formKey });
    const { errors } = lintSummary(issues);
    if (errors > 0) {
      toast(`Không phát hành được: bản nháp còn ${errors} lỗi logic cần sửa trước.`);
      return;
    }
    if (!window.confirm(`Phát hành v${v.versionNumber}? Phiếu tạo mới từ giờ sẽ dùng bộ câu hỏi này; phiếu cũ giữ nguyên bộ câu hỏi của chúng.`)) return;
    try {
      await api.forms.publish(versionId);
      toast(`Đã phát hành v${v.versionNumber}`);
      await load(formKey);
    } catch (err) {
      toast('Không phát hành được: ' + err.message);
    }
  }

  // ------------------------------------------------------------------ nối dây
  container.querySelector('#bFormKey').addEventListener('change', (e) => {
    if (dirty && !window.confirm('Bỏ các thay đổi chưa lưu và chuyển sang bộ câu hỏi khác?')) {
      e.target.value = formKey;
      return;
    }
    load(e.target.value);
  });
  container.querySelector('#bAddGroup').addEventListener('click', () => openGroupEditor({ kind: 'group' }));
  container.querySelector('#bAddRepeat').addEventListener('click', () => openGroupEditor({ kind: 'repeat' }));
  container.querySelector('#bAddQuestion').addEventListener('click', () => openQuestionEditor({}));
  container.querySelector('#bSaveBtn').addEventListener('click', save);
  container.querySelector('#bReload').addEventListener('click', () => {
    if (dirty && !window.confirm('Bỏ các thay đổi chưa lưu?')) return;
    load(formKey);
  });

  await load('onfarm');
}
